import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import type { GroupBase, OptionsOrGroups } from "react-select";
import AsyncSelect from "react-select/async";
import type { priceSearchResultType, Translationtype } from "utils";
import { getCityAutocomplete, getClientLatLong } from "../services";

interface IFormAutocompleteProps {
  setValue: any;
  register: any;
  errors: any;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  type?: string;
  name: string;
  title: string;
  tooltip?: string;
  t: Translationtype;
  control: any;
  clearData: () => void;
  initialPlaceHolder?: string;
  isRequired?: boolean;
  getValues: any;
}

export interface OptionValue {
  label: string;
  value: string;
  id: string;
}

export function FormAutocomplete({
  errors,
  setValue,
  className,
  name,
  title,
  t,
  clearData,
  control,
  isRequired = false,
  initialPlaceHolder,
  disabled,
  getValues,
}: IFormAutocompleteProps) {
  const [loading, setLoading] = useState(false);
  const [placeHolderText, setPlaceHolderText] = useState(initialPlaceHolder);
  const [prefilledCities, setPrefilledCities] = useState<
    priceSearchResultType[]
  >([]);
  const [refreshPrefilledCities] = useState(new Date(Date.now()));
  const [showDefaults, setShowDefaults] = useState(true);
  const [prefilledFoundCities, setPrefilledFoundCities] = useState<
    priceSearchResultType[]
  >([]);

  useEffect(() => {
    setPlaceHolderText(initialPlaceHolder);
  }, [initialPlaceHolder]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getClientLatLong();
        if (!response?.data) {
          return;
        }
        const latLong = response.data.split(",");
        const clientPlaces = await getCityAutocomplete(
          `${latLong[1]},${latLong[0]}`
        );
        if (!clientPlaces?.features?.length) {
          return;
        }
        if (!getValues(name)) {
          const value =
            clientPlaces?.features?.find((e) => e.id.includes("place")) ||
            clientPlaces?.features?.[0];
          setValue(name, value);
        } else {
          setShowDefaults(false);
        }
        setPrefilledCities(clientPlaces?.features);
      } catch (error) {
        console.error("Error fetching prefilled cities:", error);
      }
    };
    fetchData();
  }, [refreshPrefilledCities]);

  const searchCities = async (
    inputValue: string,
    callback: (
      options: OptionsOrGroups<
        priceSearchResultType,
        GroupBase<priceSearchResultType>
      >
    ) => void
  ): Promise<any> => {
    clearData();
    setPlaceHolderText("");
    setLoading(true);
    try {
      // When input is empty, show prefilled cities instead
      if (!inputValue.trim() && prefilledCities.length > 0) {
        callback(prefilledCities);
        return;
      }
      setShowDefaults(false);
      const data = await getCityAutocomplete(inputValue, true);
      const featureData = (data?.features ?? []) as priceSearchResultType[];
      setPrefilledFoundCities(featureData);
      callback(featureData);
    } catch (error) {
      console.error("Error fetching cities:", error);
      callback([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${className || ""} w-full`}>
      <label htmlFor={name} className="w-full block mb-1 text-textColor">
        {title}
        {isRequired ? <span className="text-red-500"> *</span> : null}
      </label>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <AsyncSelect
            defaultOptions={
              showDefaults ? prefilledCities : prefilledFoundCities
            }
            {...field}
            inputId={name}
            loadOptions={searchCities}
            isDisabled={disabled}
            isClearable
            getOptionLabel={(option) => option.place_name}
            getOptionValue={(option) => option.id}
            unstyled
            classNames={{
              control: ({ isDisabled, isFocused }) =>
                `input w-full min-h-[48px] px-4 ${
                  isDisabled ? "!bg-gray-200" : ""
                } ${isFocused ? "!border-2 !border-borderColor/40" : ""}`,
              valueContainer: () => "p-0",
              input: () => "m-0 p-0",
              indicatorsContainer: () => "h-[48px]",
              menu: () =>
                "z-[9999] mt-1 rounded-2xl border border-gray-200 bg-white shadow-md",
              option: ({ isFocused, isSelected }) =>
                `px-4 py-2 cursor-pointer ${
                  isSelected ? "bg-gray-100" : ""
                } ${isFocused ? "bg-gray-50" : ""}`,
            }}
            placeholder={placeHolderText || undefined}
            onChange={(newValue, actionMeta) => {
              if (actionMeta.action === "clear") {
                // Handle clear event here
                clearData();
                setShowDefaults(true);
              }
              field.onChange(newValue); // Call the field's onChange function
            }}
          />
        )}
      />

      {errors[name]?.message ? (
        <p className="text-sm my-3 text-error">{errors[name]?.message}</p>
      ) : null}
    </div>
  );
}
